// Phase 38 — the enriched, two-column Member profile (redesign of the
// Phase 37 consolidated detail page). Keyed by contactId. Layout: header
// (avatar/name/status/billing/joined badges + account/phone/role chips),
// action bar (added in this plan's Task 3), then a two-column body —
// main (~2/3): stat tiles, running-plan progress, attendance heatmap,
// transactions; side (~1/3): payment-due, membership, plans & passes.
// Route registration (/members/:id) is unchanged from Phase 37-06.
import { useMemo, useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetContact,
  useGetContactStats,
  useGetContactTransactions,
  useListAccountMemberships,
  useGetAccountPlanSchedule,
  useCreateAttendanceSession,
  useUpdateAccountMembership,
  useUpdateAccountContact,
  getGetContactQueryKey,
  getGetContactStatsQueryKey,
  getGetContactTransactionsQueryKey,
  getListAccountMembershipsQueryKey,
  getGetAccountPlanScheduleQueryKey,
  type Membership,
  type PlanSchedulePeriod,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  ExternalLink,
  IndianRupee,
  RefreshCw,
  UserCheck,
  Settings,
  Power,
  MessageCircle,
  Pencil,
  ArrowRightLeft,
  Bell,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { normalizePhone } from "@/lib/phone";
import { MemberPlanCards } from "@/components/members/member-plan-cards";
import { MemberTransactionsPanel } from "@/components/members/member-transactions-panel";
import { MemberStatTiles } from "@/components/members/member-stat-tiles";
import { AttendanceHeatmap } from "@/components/members/attendance-heatmap";
import {
  RunningPlanProgress,
  type RunningPlanProgressPlan,
} from "@/components/members/running-plan-progress";
import { MemberCollectPayment } from "@/components/members/member-collect-payment";
import { MemberCollectCharges } from "@/components/members/member-collect-charges";
import { ReassignAccountDialog } from "@/components/members/reassign-account-dialog";
import { ExitPlanDialog } from "@/components/plan-exit/exit-plan-dialog";
import { sumChargesDue } from "@/lib/charge-collection";
import { format } from "date-fns";

/** "Payment" is the display label for the `billing` role (CONTEXT: no schema
 *  change — Roles stay primary/billing/user everywhere else in the app). */
const ROLE_LABELS: Record<string, string> = {
  primary: "Primary",
  billing: "Payment",
  user: "Member",
};

/** INR formatter — mirrors account-detail.tsx's formatINR (no shared
 *  @/lib/format money fn exists in this codebase yet). */
function formatINR(amount: number | null | undefined): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(n)}`;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return format(new Date(d), "d MMM yyyy");
}

/** Grouped running-plan shape this file derives + Deactivate's PATCH target. */
type RunningPlan = RunningPlanProgressPlan & { membershipIds: number[] };

/**
 * Derives the member's currently RUNNING plan for the Running-plan progress
 * card (+ header Recurring/One-time badge) from the account's plan-schedule,
 * scoped to this contact's ACTIVE memberships only.
 *
 * Picks the in-progress cycle (periodStart <= today <= periodEnd); falls
 * back to the most-recently-started period, then the soonest upcoming one,
 * when nothing is exactly "in progress" today (e.g. between cycles). Plan
 * membership.startDate/endDate can't be used directly for this — Recurring
 * memberships have a NULL endDate (open-ended), so the day-X-of-N window has
 * to come from the already-computed plan-schedule period, not the raw
 * membership row.
 *
 * Combines sibling lines sharing the same quoteGroupId AND the same cycle
 * window into ONE grouped card — mirrors the "Plan #{firstLineId}" deal
 * grouping convention used everywhere else (member-plan-cards.tsx,
 * account-detail.tsx Plan tab/Transactions). "id" = the lowest membershipId
 * among the combined siblings (first-line).
 */
function deriveRunningPlan(
  periods: PlanSchedulePeriod[],
  memberships: Membership[],
  contactId: number,
): RunningPlan | null {
  const activeIds = new Set(
    memberships
      .filter((m) => m.contactId === contactId && m.status === "active")
      .map((m) => m.id),
  );
  const mine = periods.filter(
    (p) => p.contactId === contactId && activeIds.has(p.membershipId),
  );
  if (mine.length === 0) return null;

  const dateKey = (d: string | Date) => format(new Date(d), "yyyy-MM-dd");
  const todayKey = dateKey(new Date());

  const inProgress = mine.filter(
    (p) => dateKey(p.periodStart) <= todayKey && todayKey <= dateKey(p.periodEnd),
  );
  let anchor: PlanSchedulePeriod | undefined = inProgress[0];
  if (!anchor) {
    const started = mine
      .filter((p) => dateKey(p.periodStart) <= todayKey)
      .sort((a, b) => dateKey(b.periodStart).localeCompare(dateKey(a.periodStart)));
    anchor = started[0];
  }
  if (!anchor) {
    const upcoming = [...mine].sort((a, b) =>
      dateKey(a.periodStart).localeCompare(dateKey(b.periodStart)),
    );
    anchor = upcoming[0];
  }
  if (!anchor) return null;

  const groupKey = anchor.quoteGroupId ?? `solo-${anchor.membershipId}`;
  const anchorStartKey = dateKey(anchor.periodStart);
  const siblings = mine.filter(
    (p) =>
      (p.quoteGroupId ?? `solo-${p.membershipId}`) === groupKey &&
      dateKey(p.periodStart) === anchorStartKey,
  );

  const firstLineId = Math.min(...siblings.map((s) => s.membershipId));
  const names = Array.from(
    new Set(siblings.map((s) => s.planName).filter(Boolean)),
  ) as string[];
  const price = siblings.reduce((sum, s) => sum + Number(s.amount ?? 0), 0);
  const periodEnd = siblings.reduce(
    (max, s) => (new Date(s.periodEnd) > new Date(max) ? s.periodEnd : max),
    anchor.periodEnd,
  );

  return {
    id: firstLineId,
    name: names.join(" + ") || "Plan",
    cycleMode: anchor.cycleMode,
    periodStart: anchor.periodStart,
    periodEnd,
    price,
    autoRenewDate: anchor.cycleMode === "One_Time" ? null : periodEnd,
    membershipIds: Array.from(new Set(siblings.map((s) => s.membershipId))),
  };
}

export default function MemberProfile() {
  const params = useParams<{ id: string }>();
  const contactId = Number(params.id);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  // ── Action-bar state (Task 3) ──────────────────────────────────────────
  const [collectOpen, setCollectOpen] = useState(false);
  // Phase 39 — grouped "Charges due" collect (SEPARATE rail from the Plan
  // Collect above; never merged).
  const [collectChargesOpen, setCollectChargesOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const createSession = useCreateAttendanceSession();
  const updateMembership = useUpdateAccountMembership();
  const updateContact = useUpdateAccountContact();

  const {
    data: contact,
    isLoading,
    isError,
  } = useGetContact(contactId, {
    query: {
      queryKey: getGetContactQueryKey(contactId),
      enabled: Number.isFinite(contactId),
    },
  });

  const accountId = contact?.accountId;

  const { data: stats } = useGetContactStats(contactId, {
    query: {
      queryKey: getGetContactStatsQueryKey(contactId),
      enabled: Number.isFinite(contactId),
    },
  });

  // Account-scoped memberships + plan-schedule — feeds the Running-plan
  // progress card + header Recurring/One-time badge + Deactivate's target
  // membership id(s). Shares a query-key/cache with MemberPlanCards' and
  // MemberCollectPayment's own internal fetches (no duplicate network hit
  // when those are also mounted).
  const { data: memberships } = useListAccountMemberships(accountId ?? 0, {
    query: {
      queryKey: getListAccountMembershipsQueryKey(accountId ?? 0),
      enabled: !!accountId,
    },
  });
  const { data: scheduleData } = useGetAccountPlanSchedule(accountId ?? 0, {
    query: {
      queryKey: getGetAccountPlanScheduleQueryKey(accountId ?? 0),
      enabled: !!accountId,
    },
  });

  const runningPlan = useMemo(
    () =>
      deriveRunningPlan(
        (scheduleData?.periods ?? []) as PlanSchedulePeriod[],
        (memberships ?? []) as Membership[],
        contactId,
      ),
    [scheduleData, memberships, contactId],
  );

  const pendingDues = Number(stats?.pendingDues ?? 0);

  // Phase 39 — "Charges due" total for the non-Plan (member_charges) rail.
  // Client-derived from the SAME useGetContactTransactions call
  // MemberTransactionsPanel already makes (React Query dedupes, no new
  // endpoint) — pendingDues above stays Plan-only/untouched (SC4).
  const { data: txns } = useGetContactTransactions(contactId, {
    query: {
      queryKey: getGetContactTransactionsQueryKey(contactId),
      enabled: Number.isFinite(contactId),
    },
  });
  const chargesDue = useMemo(() => sumChargesDue(txns), [txns]);

  // "Last activity" — the most recent heatmap day with any visit, derived
  // client-side from the already-fetched ContactStats.heatmap (no new
  // endpoint; mirrors attendance-heatmap.tsx's own client-side bucketing).
  const lastActivityDate = useMemo(() => {
    const cells = stats?.heatmap ?? [];
    const withVisits = cells.filter((c) => c.count > 0);
    if (withVisits.length === 0) return null;
    return withVisits.reduce((latest, c) =>
      new Date(c.date) > new Date(latest.date) ? c : latest,
    ).date;
  }, [stats]);

  // ── Action-bar handlers (Task 3) ────────────────────────────────────────
  // Check in — manual attendance punch, reusing Phase 33's
  // createAttendanceSession ("Manually create a session — e.g. a missed
  // punch"). Invalidates ContactStats (drives both MemberStatTiles' Total
  // Visits AND AttendanceHeatmap — same query key, one invalidate covers
  // both) so the profile reflects the new visit without a manual refresh.
  async function handleCheckIn() {
    try {
      await createSession.mutateAsync({
        data: {
          contactId,
          entryAt: new Date().toISOString(),
          note: "Manual check-in (member profile)",
        },
      });
      toast({ title: "Checked in." });
      qc.invalidateQueries({ queryKey: getGetContactStatsQueryKey(contactId) });
    } catch {
      toast({ title: "Couldn't check in.", variant: "destructive" });
    }
  }

  // Deactivate — soft-exits every membership in the member's running-plan
  // group (PATCH status:'exited'; NEVER delete — preserves Paid periods).
  // Shortcut for Manage membership → Status Inactive (per CONTEXT). Now asks
  // for an exit date (the shared ExitPlanDialog); once the last active plan
  // exits, the backend flips contacts.status → exited so the header badge
  // reads "Deactivated" and attendance drops them next month.
  async function confirmDeactivate(exitDate: string) {
    if (!accountId || !runningPlan || runningPlan.membershipIds.length === 0) return;
    try {
      // Sequential (not Promise.all) so the backend's last-active-plan probe
      // sees each prior line already committed.
      for (const membershipId of runningPlan.membershipIds) {
        await updateMembership.mutateAsync({
          id: accountId,
          membershipId,
          data: { status: "exited", endDate: exitDate },
        });
      }
      toast({ title: "Member deactivated." });
      qc.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      qc.invalidateQueries({ queryKey: getGetAccountPlanScheduleQueryKey(accountId) });
      qc.invalidateQueries({ queryKey: getGetContactQueryKey(contactId) });
      setDeactivateOpen(false);
    } catch {
      toast({ title: "Couldn't deactivate.", variant: "destructive" });
    }
  }

  // WhatsApp / Send reminder — client-only wa.me deep links (no backend send;
  // no reminder template exists yet, per RESEARCH Pitfall 5 / CONTEXT v1 call).
  function openWhatsApp(text?: string) {
    if (!contact) return;
    const digits = normalizePhone(contact.phone);
    if (!digits) {
      toast({ title: "No valid phone number on file.", variant: "destructive" });
      return;
    }
    const url = `https://wa.me/91${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function handleSendReminder() {
    if (!contact) return;
    const amount = formatINR(pendingDues);
    openWhatsApp(
      `Hi ${contact.fullName}, this is a reminder that ${amount} is due on your membership. Please clear it at your earliest convenience. Thank you!`,
    );
  }

  function openEditDialog() {
    if (!contact) return;
    setEditName(contact.fullName);
    setEditPhone(contact.phone);
    setEditEmail(contact.email ?? "");
    setEditOpen(true);
  }

  async function handleEditSubmit() {
    if (!accountId) return;
    try {
      await updateContact.mutateAsync({
        id: accountId,
        contactId,
        data: {
          fullName: editName,
          phone: editPhone,
          email: editEmail || null,
        },
      });
      toast({ title: "Profile updated." });
      qc.invalidateQueries({ queryKey: getGetContactQueryKey(contactId) });
      setEditOpen(false);
    } catch {
      toast({ title: "Couldn't update profile.", variant: "destructive" });
    }
  }

  if (!Number.isFinite(contactId)) {
    return <div className="p-6 text-muted-foreground">Invalid member id</div>;
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 p-1">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full rounded-md" />
        <Skeleton className="h-32 w-full rounded-md" />
      </div>
    );
  }

  if (isError || !contact) {
    return (
      <div className="mx-auto max-w-6xl p-6 text-center text-muted-foreground">
        Member not found.{" "}
        <button className="text-primary underline" onClick={() => navigate("/members")}>
          Back to Members
        </button>
      </div>
    );
  }

  const billingBadgeLabel = runningPlan
    ? runningPlan.cycleMode === "One_Time"
      ? "One-time"
      : "Recurring"
    : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-1">
      <button
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onClick={() => navigate("/members")}
      >
        <ArrowLeft className="h-4 w-4" /> All members
      </button>

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          {contact.photoUrl ? (
            <img
              src={contact.photoUrl}
              alt={contact.fullName}
              className="h-14 w-14 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-lg font-semibold text-muted-foreground">
              {contact.fullName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold">{contact.fullName}</h1>
              <Badge variant={contact.status === "active" ? "default" : "secondary"}>
                {contact.status === "active" ? "Active" : "Deactivated"}
              </Badge>
              {billingBadgeLabel && <Badge variant="outline">{billingBadgeLabel}</Badge>}
              {stats?.joinedAt && (
                <Badge variant="outline">Joined {fmtDate(stats.joinedAt)}</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <Link
                href={`/accounts/${contact.accountId}`}
                className="flex items-center gap-1 font-medium text-foreground hover:underline"
              >
                {contact.accountLegalName}
                <ExternalLink className="h-3 w-3" />
              </Link>
              <span>{contact.phone}</span>
              <span className="flex flex-wrap gap-1">
                {contact.roles.length === 0 ? (
                  <span>—</span>
                ) : (
                  contact.roles.map((r) => (
                    <Badge key={r} variant="outline" className="text-xs">
                      {ROLE_LABELS[r] ?? r}
                    </Badge>
                  ))
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        {pendingDues > 0 && (
          <Button onClick={() => setCollectOpen(true)}>
            <IndianRupee className="mr-1.5 h-4 w-4" />
            Collect {formatINR(pendingDues)}
          </Button>
        )}
        {chargesDue > 0 && (
          <Button variant="outline" onClick={() => setCollectChargesOpen(true)}>
            <IndianRupee className="mr-1.5 h-4 w-4" />
            Collect charges {formatINR(chargesDue)}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => navigate(`/accounts/${contact.accountId}/add-plan?contactId=${contactId}`)}
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Renew plan
        </Button>
        <Button variant="outline" onClick={handleCheckIn} disabled={createSession.isPending}>
          {createSession.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <UserCheck className="mr-1.5 h-4 w-4" />
          )}
          Check in
        </Button>
        <Button variant="outline" onClick={() => navigate(`/members/${contactId}/manage`)}>
          <Settings className="mr-1.5 h-4 w-4" />
          Manage membership
        </Button>
        <Button
          variant="outline"
          onClick={() => setDeactivateOpen(true)}
          disabled={contact.status !== "active" || updateMembership.isPending}
        >
          <Power className="mr-1.5 h-4 w-4" />
          Deactivate
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="text-green-600 hover:text-green-700"
          title="WhatsApp"
          onClick={() => openWhatsApp()}
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" title="Edit" onClick={openEditDialog}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" title="Reassign to another account" onClick={() => setReassignOpen(true)}>
          <ArrowRightLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* ── Main / Side two-column layout ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column (~2/3) */}
        <div className="space-y-6 lg:col-span-2">
          <MemberStatTiles contactId={contactId} />
          <RunningPlanProgress plan={runningPlan} />
          <AttendanceHeatmap contactId={contactId} />
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <MemberTransactionsPanel contactId={contactId} accountId={contact.accountId} />
            </CardContent>
          </Card>
        </div>

        {/* Side column (~1/3) */}
        <div className="space-y-6">
          {pendingDues > 0 && (
            <Card className="border-orange-300 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-orange-700">
                  Payment due
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-2xl font-bold tabular-nums text-orange-700">
                  {formatINR(pendingDues)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Oldest due {fmtDate(stats?.oldestDueDate)}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setCollectOpen(true)}>
                    Collect payment
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleSendReminder}>
                    <Bell className="mr-1.5 h-3.5 w-3.5" />
                    Send reminder
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Phase 39 — grouped "Charges due" card: the non-Plan
              (member_charges) rail, visually distinct (indigo) from the
              Plan "Payment due" card above (orange), never merged. */}
          {chargesDue > 0 && (
            <Card className="border-indigo-300 bg-indigo-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-indigo-700">
                  Charges due
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-2xl font-bold tabular-nums text-indigo-700">
                  {formatINR(chargesDue)}
                </p>
                <p className="text-xs text-muted-foreground">
                  F&amp;B, prints, stationery, booking &amp; conference-room charges
                </p>
                <Button size="sm" onClick={() => setCollectChargesOpen(true)}>
                  Collect charges
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Membership</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium">
                  {contact.status === "active" ? "Active" : "Deactivated"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billing</span>
                <span className="font-medium">{billingBadgeLabel ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Account</span>
                <span className="font-medium">{contact.accountLegalName}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Roles</span>
                <span className="flex flex-wrap justify-end gap-1">
                  {contact.roles.map((r) => (
                    <Badge key={r} variant="outline" className="text-xs">
                      {ROLE_LABELS[r] ?? r}
                    </Badge>
                  ))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last activity</span>
                <span className="font-medium">{fmtDate(lastActivityDate)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Phase 58 (POSPAY-01) — member payment identity: the typed UPI VPA
              plus the masked form a Pine Labs export will carry, so the
              operator can eyeball the match themselves. A member with no VPA
              is flagged rather than shown an empty row (display-only — plain
              markup, no FormLabel/FormField ancestor here). */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Payment identity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {contact.upiVpa ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">UPI ID</span>
                    <span className="font-medium">{contact.upiVpa}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Masked (Pine Labs form)</span>
                    <span className="font-mono font-medium">{contact.upiVpaMasked ?? "—"}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-amber-700">
                  No UPI ID on file — payments from this member cannot be auto-matched.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Plans &amp; passes</CardTitle>
            </CardHeader>
            <CardContent>
              <MemberPlanCards accountId={contact.accountId} contactId={contactId} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Action-bar dialogs ── */}
      <MemberCollectPayment
        accountId={contact.accountId}
        contactId={contactId}
        open={collectOpen}
        onClose={() => setCollectOpen(false)}
      />
      <MemberCollectCharges
        accountId={contact.accountId}
        contactId={contactId}
        open={collectChargesOpen}
        onClose={() => setCollectChargesOpen(false)}
      />
      <ReassignAccountDialog
        contactId={contactId}
        currentAccountId={contact.accountId}
        currentRoles={contact.roles}
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
      />
      {/* Deactivate = soft-exit the member's running plan as of a chosen date. */}
      <ExitPlanDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        onConfirm={confirmDeactivate}
        isPending={updateMembership.isPending}
        isDeal={(runningPlan?.membershipIds.length ?? 0) > 1}
        verb="Deactivate"
      />
      <Dialog open={editOpen} onOpenChange={(v) => { if (!v) setEditOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>Update this member&apos;s basic profile fields.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Full name</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input id="edit-phone" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={updateContact.isPending || !editName || !editPhone}>
              {updateContact.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
